import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { ReleaseNoteService } from './release-note.service';
import { UserService } from './user.service';
import { BaseService } from '@app/core/services/base.service';
import { firstValueFrom } from 'rxjs';
import { isReachableProbeResponse } from '@app/core/utils/probe-reachability.util';

@Injectable({
  providedIn: 'root',
})
export class VersionCheckService extends BaseService {
  constructor(
    public http: HttpClient,
    private releaseNoteService: ReleaseNoteService,
    private userService: UserService,
    private router: Router,
    private snackbar: MatSnackBar,
    private translateService: TranslateService,
  ) {
    super(http);
  }

  async probeVersion(): Promise<boolean> {
    try {
      await firstValueFrom(this.releaseNoteService.checkVersionUpdate());
      return true;
    } catch (error: unknown) {
      if (isReachableProbeResponse(error)) {
        return true;
      }
      return false;
    }
  }

  async checkVersion(): Promise<void> {
    const versionCheck = await firstValueFrom(this.releaseNoteService.checkVersionUpdate());
    if (versionCheck.updateAvailable) {
      const message =
        this.translateService.instant('versionCheck.newVersionAvailable', {
          version: versionCheck.currentVersion,
        }) || `New version ${versionCheck.currentVersion} is available!`;

      const action = this.translateService.instant('versionCheck.viewReleaseNotes') || 'View Release Notes';
      const snackBarRef = this.snackbar.open(message, action, {
        duration: 10000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });

      snackBarRef.onAction().subscribe(async () => {
        await firstValueFrom(this.releaseNoteService.markVersionAsSeen());
        this.router.navigate(['/release-note']);
      });

      snackBarRef.afterDismissed().subscribe(async (dismissInfo) => {
        if (dismissInfo.dismissedByAction) {
          return;
        }
        await firstValueFrom(this.releaseNoteService.markVersionAsSeen());
      });
    }
  }
}
